import _ from 'lodash'
import Backbone from 'backbone'
import bookmarks from 'scoreboard/bookmarks/bookmarks'
import constants from 'scoreboard/components/constants'
import permissions from 'scoreboard/components/permissions'
import UserModel from 'scoreboard/admin/principals/users/user'
import unwrapModel from 'scoreboard/components/unwrap-model'
import projects from 'scoreboard/projects/projects'

class User {

	/**
	 * Current user id
	 */
	get id() {
		return _.get(this.model, 'id')
	}

	get label() {
		return _.get(this.model, 'attributes.label')
	}

	/**
	 * A principal object representing the current user
	 */
	get principal() {
		return {
			id: this.id,
			type: 'user',
			label: this.label
		}
	}

	/**
	 * @return {UserModel} The user who is impersonating this user, or undefined
	 * if no impersonation is active.
	 */
	get impersonator() {
		return _impersonator.model
	}

	get isImpersonationActive() {
		return !_.isNil(this.impersonator)
	}

	/**
	 * @returns {Object} an unwrapped user model.  If the current user is being
	 * impersonated the id of the user who initiated the impersonation is returned,
	 * otherwise the current user is returned.
	 */
	get impersonatorOrUser() {
		return unwrapModel(this.impersonator || this.model)
	}

	/**
	 * This just checks top level permissions for whether or not to show the
	 * "log in as" button on the form.
	 *
	 * Additional checks are done in SecurityChecker.java#ensureImpersonable
	 * based on a full comparison of user permissions.
	 *
	 * We do this on demand to delay the cost of calculating user permissions.
	 *
	 * @returns true if the current user can impersonate someone else
	 */
	canImpersonate(userId) {
		let isUser = userId === this.model.get('id')
		// https://github.com/SpiderStrategies/Scoreboard/issues/27957#issuecomment-574323881
		// #34993 check for the "log in as other users" permission
		return permissions.impersonate() &&
			// If already impersonating a user can't impersonate someone else
			!this.impersonator &&
			// #32806 user should not be able to use the "log in as" button for themselves
			!isUser
	}

	isStrictViewer() {
		//the public read only user is always a strict viewer
		return permissions.isStrictViewer() || _.get(this.model, 'publicReadOnlyUser', false)
	}

	isAnonymous() {
		return this.id == constants.USER_ANONYMOUS_ID
	}

	isBuiltInAdmin() {
		return this.id == constants.USER_ADMIN_ID
	}
	/**
	 * Check for user equality
	 * @param {User|Number} otherUser can be a User Model instance, or a userId
	 * @return {boolean}
	 */
	equals(otherUser) {
		return this.model.id == _.get(otherUser, 'id', otherUser)
	}
}
const _user = new User()
const _impersonator = new User()

Backbone.on('load', ({user, userImpersonator}) => load(user, userImpersonator))

function load(user, userImpersonator) {
	delete _impersonator.model
	if (user) {
		_user.model = new UserModel(user)
		if (userImpersonator) {
			_impersonator.model = new UserModel(userImpersonator)
			Backbone.trigger('impersonate', user)
		}
	}
}

/**
 * Takes care of reassining all global state when a user changes during impersonation
 * (projects, bookmarks, alert counts)
 *
 * @param data.projectId
 * @param data.projects
 * @param data.bookmarkUrls
 * @param data.unreadAlertCount
 *
 * @see com.spider.cms.admin.api.UsersController.globalUserState
 */
function assignState(data) {
	projects.update(data.projects, data.projectId)
	bookmarks.load(data.bookmarkUrls)
	Backbone.trigger('change:unread', {numUnread: data.unreadAlertCount})
}

_.extend(_user, {
	assignState,
	load
})
export default _user
export { load }
export { assignState }

_user.__filename__ = 'components/user.js'
